import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useDispatch } from "react-redux";
import { setUser } from "../store/userSlice";

const API_URL = process.env.REACT_APP_API_URL;
const HEADER_LOGO =
  "/assets/img/Website_logo_color logo-black text-1.png";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const DEFAULT_ROLE = process.env.REACT_APP_DEFAULT_ROLE;
const COMPANY_ICON = "/assets/img/color icon_circle.png";
const ANDROID_QR = "/assets/img/qr-code.png";
const PLAY_STORE_URL =
  "https://play.google.com/store/search?q=Instatenders&c=apps&hl=en_IN";
const COMPANY_ADDRESS = [
  "4/159, Labour Colony 1st St, 3rd Phase,",
  "Thiru Vi Ka Industrial Estate, SIDCO Industrial Estate,",
  "Guindy, Chennai,",
  "Tamil Nadu 600032.",
];

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentYear = new Date().getFullYear();
  const googleBtnRef = useRef(null);


  // Start at the top when arriving from another page (e.g. Our Listings)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

    const handleLoginSuccess = useCallback((user, extra = {}) => {
    localStorage.clear();

    const permissions = Array.isArray(user.permissions) ? user.permissions : [];
    const moduleAccess = Array.isArray(user.moduleAccess)
      ? user.moduleAccess
      : Array.isArray(extra.moduleAccess)
        ? extra.moduleAccess
        : [];
    const businessId = user.businessId || null;

    const role = user.role || DEFAULT_ROLE;
    const supplierId =
      String(role).toLowerCase() === "supplier" && user.supplierId
        ? user.supplierId
        : null;

    const customerId =
       String(role).toLowerCase() === "customer" && user.customerId
       ? user.customerId
       : null;

    localStorage.setItem("role", role);
    localStorage.setItem("permissions", JSON.stringify(permissions));
    localStorage.setItem("moduleAccess", JSON.stringify(moduleAccess));
    localStorage.setItem("id", user.id);
    if (businessId) {
      localStorage.setItem("businessId", businessId);
    }
    if (supplierId) {
      localStorage.setItem("supplierId", supplierId);
    }
    if (customerId) {
       localStorage.setItem("customerId", customerId);
     }
    localStorage.setItem(
      "user",
      JSON.stringify({
        id: user.id,
        name: user.name || "",
        role,
        businessId,
        ...(supplierId ? { supplierId } : {}),
        ...(customerId ? { customerId } : {}),

      })
    );

    dispatch(
      setUser({
        id: user.id,
        name: user.name || "",
        role: role || "supplier",
        businessId,
        ...(supplierId ? { supplierId } : {}),
        ...(customerId ? { customerId } : {}),
        permissions,
        moduleAccess,
      }),
    );

    navigate("/dashboard");
  }, [dispatch, navigate]);

  const extractErrorMessage = (error, fallback) => {
    if (error.response && error.response.data) {
      let errorData = error.response.data;
      if (typeof errorData === "string") {
        try {
          errorData = JSON.parse(errorData);
        } catch (e) {}
      }
      return errorData.message || fallback;
    }
    return "Network error. Please check your connection and try again.";
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    setErrorMessage("");

    if (!email || !password) {
      setErrorMessage("Please enter both email and password");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(API_URL, {
        action: "login",
        email: email,
        password: password,
      });

      let responseData = response.data;
      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      if (
        responseData.success === true &&
        responseData.user &&
        responseData.user.id
      ) {
         const user = {
           ...responseData.user,
           moduleAccess:
             responseData.user.moduleAccess ||
             responseData.moduleAccess ||
             responseData.data?.moduleAccess ||
             [],
         };
         handleLoginSuccess(user);
         
      } else {
        setErrorMessage(
          responseData.message ||
            "Login failed. Please check your credentials.",
        );
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error.response && error.response.data) {
        let errorData = error.response.data;
        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {}
        }
        setErrorMessage(
          errorData.message ||
            "An error occurred during login. Please try again.",
        );
      } else {
        setErrorMessage(
          "Network error. Please check your connection and try again.",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCredentialResponse = useCallback(async (response) => {
    setErrorMessage("");
    setIsGoogleLoading(true);

    try {
      const apiResponse = await axios.post(API_URL, {
        action: "google-login",
        idToken: response.credential,
        role: DEFAULT_ROLE,
      });

      let responseData = apiResponse.data;
      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = apiResponse.data;
        }
      }

      if (
        responseData.success === true &&
        responseData.user &&
        responseData.user.id
      ) {
        const user = {
          ...responseData.user,
          moduleAccess:
            responseData.user.moduleAccess ||
            responseData.moduleAccess ||
            responseData.data?.moduleAccess ||
            [],
        };
        handleLoginSuccess(user);
      } else {
        setErrorMessage(
          responseData.message || "Google sign-in failed. Please try again.",
        );
      }
    } catch (error) {
      console.error("Google login error - full details:", error);
      console.error("Error response data:", error.response?.data);
      console.error("Error status:", error.response?.status);
      setErrorMessage(
        extractErrorMessage(
          error,
          "An error occurred during Google sign-in. Please try again.",
        ),
      );
    } finally {
      setIsGoogleLoading(false);
    }
  }, [handleLoginSuccess]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      console.error("REACT_APP_GOOGLE_CLIENT_ID is not set");
      return;
    }

    const scriptId = "google-identity-script";
    let script = document.getElementById(scriptId);

    const initializeGoogle = () => {
      if (!window.google || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredentialResponse,
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "outline",
        size: "large",
        text: "signin_with",
        width: 320,
      });
    };

    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogle;
      document.body.appendChild(script);
    } else {
      initializeGoogle();
    }
  }, [handleGoogleCredentialResponse]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (errorMessage) setErrorMessage("");
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (errorMessage) setErrorMessage("");
  };

  return (
    <div className="auth-shell accent-custom">
      <header className="auth-header">
        <div className="auth-header-inner">
          <a href="/" className="auth-brand" aria-label="InstaTenders Home">
            <img src={HEADER_LOGO} alt="InstaTenders" className="auth-brand-logo" />
          </a>

          <nav className="auth-nav">
            <Link to="/" className="auth-nav-link">
              Our Listings
            </Link>
            <Link to="/po-tracking" className="auth-nav-link">
              Track Purchase Order
            </Link>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="auth-store-btn"
            >
              <i className="fa-brands fa-google-play" aria-hidden="true" />
              Android
            </a>
          </nav>
        </div>
      </header>

      <main className="auth-main">
        <div className="auth-main-inner">
          <section className="auth-hero" id="our-listings">
            <span className="auth-eyebrow">
              <i className="fa-solid fa-circle-check" /> Trusted B2B Procurement
              Platform
            </span>
            <h1>
              One platform for your entire
              <span> procure-to-pay </span>
              lifecycle
            </h1>
            <p>
              InstaTenders Supply Master brings purchase requests, RFQs,
              purchase orders, supplier quotes, inventory, and sales together —
              so your team works from a single source of truth.
            </p>

            <div className="auth-module-grid">
              <div className="auth-module-card">
                <div className="auth-module-icon icon-teal">
                  <i className="fa-solid fa-file-invoice" />
                </div>
                <div className="auth-module-text">
                  <strong>Procurement</strong>
                  <small>PR, RFQ, PO &amp; supplier quotes in one flow</small>
                </div>
              </div>
              <div className="auth-module-card">
                <div className="auth-module-icon icon-navy">
                  <i className="fa-solid fa-warehouse" />
                </div>
                <div className="auth-module-text">
                  <strong>Inventory</strong>
                  <small>Live stock levels with full audit history</small>
                </div>
              </div>
              <div className="auth-module-card">
                <div className="auth-module-icon icon-green">
                  <i className="fa-solid fa-chart-line" />
                </div>
                <div className="auth-module-text">
                  <strong>Sales &amp; Quotes</strong>
                  <small>Customer orders, quotes &amp; payment tracking</small>
                </div>
              </div>
              <div className="auth-module-card">
                <div className="auth-module-icon icon-amber">
                  <i className="fa-solid fa-database" />
                </div>
                <div className="auth-module-text">
                  <strong>Master Data</strong>
                  <small>Suppliers, customers, materials &amp; roles</small>
                </div>
              </div>
            </div>

            <div className="auth-trust-strip">
              <div className="auth-trust-item">
                <i className="fa-solid fa-shield-halved" />
                <div>
                  <strong>Role-based access</strong>
                  <small>Granular module permissions</small>
                </div>
              </div>
              <div className="auth-trust-divider" />
              <div className="auth-trust-item">
                <i className="fa-solid fa-clock-rotate-left" />
                <div>
                  <strong>Complete audit trail</strong>
                  <small>Every change is tracked</small>
                </div>
              </div>
              <div className="auth-trust-divider" />
              <div className="auth-trust-item">
                <i className="fa-brands fa-android" />
                <div>
                  <strong>Android app</strong>
                  <small>Track Leads on the Go</small>
                </div>
              </div>
            </div>
          </section>

          <section className="auth-card-wrap">
            <div className="auth-card">
              <div className="auth-card-header">
                <h2>Sign in</h2>
                <p>Enter your email and password to continue</p>
              </div>

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="auth-field">
                  <label htmlFor="login-email">Email</label>
                  <div className="auth-input-group">
                    <i className="fa-solid fa-envelope" />
                    <input
                      id="login-email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={handleEmailChange}
                      autoComplete="email"
                      required
                    />
                  </div>
                </div>

                <div className="auth-field">
                  <label htmlFor="login-password">Password</label>
                  <div className="auth-input-group">
                    <i className="fa-solid fa-lock" />
                    <input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={handlePasswordChange}
                      autoComplete="current-password"
                      required
                    />
                    <button
                      type="button"
                      className="auth-eye-btn"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      <i
                        className={`fa-solid ${
                          showPassword ? "fa-eye-slash" : "fa-eye"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {errorMessage && (
                  <div className="auth-alert" role="alert">
                    <i className="fa-solid fa-circle-exclamation" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                <div className="auth-form-meta">
                  <Link to="/forget" className="auth-forgot">
                    Forgot Password?
                  </Link>
                </div>

                <button
                  type="submit"
                  className="auth-submit-btn"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Login
                      <i className="fa-solid fa-arrow-right" />
                    </>
                  )}
                </button>
                    <div className="auth-divider">
                  <span>or</span>
                </div>

                <div className="auth-google-btn-wrap">
                  {isGoogleLoading ? (
                    <div className="auth-google-loading">
                      <i className="fa-solid fa-spinner fa-spin" />
                      Signing in with Google...
                    </div>
                  ) : (
                    <div ref={googleBtnRef}></div>
                  )}
                </div>
              </form>
            </div>
          </section>
        </div>
      </main>

      <footer className="auth-footer">
        <div className="auth-footer-inner">
          <div className="auth-footer-brand">
            <div className="auth-footer-brand-row">
              <img
                src={COMPANY_ICON}
                alt="InstaTenders"
                className="auth-footer-logo"
              />
              <span className="auth-footer-brand-text">InstaTenders</span>
            </div>
            <div className="auth-qr-block">
              <div className="auth-qr-label">
                <span>Playstore</span>
                <i className="fa-brands fa-android" aria-hidden="true" />
              </div>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="auth-qr-link"
                title="Open InstaTenders on Google Play"
              >
                <img src={ANDROID_QR} alt="Android app QR code" />
              </a>
            </div>
          </div>

          <div className="auth-footer-address">
            <h3>Address</h3>
            <p className="auth-footer-company">InstaTenders Solutions Pvt Ltd</p>
            {COMPANY_ADDRESS.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>

          <div className="auth-footer-contact">
            <h3>Contact</h3>
            <p className="auth-footer-contact-line">
              <i className="fa-solid fa-envelope" aria-hidden="true" />
              <a href="mailto:info@instatenders.in">info@instatenders.in</a>
            </p>
            <p className="auth-footer-contact-line">
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <a href="tel:+919150081257">9150081257</a>,{" "}
              <a href="tel:+919150081260">9150081260</a>
            </p>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="auth-play-badge"
            >
              <i className="fa-brands fa-google-play" />
              <span>
                <small>GET IT ON</small>
                <strong>Google Play</strong>
              </span>
            </a>
          </div>
        </div>

        <div className="auth-footer-bottom">
          <p>
            {currentYear} © InstaTenders Solutions Pvt Ltd, All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Login;
